/**
 * 已发布模型的完整编辑表单：价格（原币种、按百万 Token、图片、视频）、思考策略、可见性，以及阶梯、分时、限时等复杂价目的 JSON 编辑。
 *
 * 背景：从旧的 Models 页拆出，Alice 版式的模型表只负责列表与行内操作，完整价目仍在这里改。
 * 设计意图：价格以字符串提交，后端按 Decimal 校验；复杂价目由后端沿用 Alice 的校验函数裁决，前端只检查是不是合法 JSON。
 * 关键约束：PUT /models/{id} 不覆盖图标、标签与白名单（这三项由 PATCH 单独改）；kind 创建后不能改。
 */
import { useState } from 'react'
import { catalogApi, type CatalogModel } from '@ap/api-client/admin-catalog'
import type { ModelInput, ModelKind, ThinkingPolicy } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import {
  Button,
  Card,
  Field,
  Form,
  Input,
  Modal,
  Notice,
  Select,
  Switch,
  Textarea,
  useToast,
} from '@ap/ui'
import { KINDS, isEmptyObj, num, parseJson, showJson } from './shared.ts'

export function ModelForm({ model, onClose, onSaved }: { model: CatalogModel | 'new' | null; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const isNew = model === 'new'
  const [id, setId] = useState('')
  const [f, setF] = useState<Record<string, string>>({})
  const [kind, setKind] = useState<ModelKind>('chat')
  const [currency, setCurrency] = useState<'USD' | 'CNY'>('USD')
  const [imageMode, setImageMode] = useState<'per_image' | 'token'>('per_image')
  const [thinking, setThinking] = useState<ThinkingPolicy>('inherit')
  const [enabled, setEnabled] = useState(true)
  const [pub, setPub] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<typeof model>(null)

  if (model !== shown) {
    setShown(model)
    setError('')
    if (model) {
      const m = model === 'new' ? null : model
      setId(m?.id ?? '')
      setKind(m?.kind ?? 'chat')
      setCurrency(m?.price_currency ?? 'USD')
      setImageMode(m?.image_billing_mode ?? 'per_image')
      setThinking(m?.thinking_policy ?? 'inherit')
      setEnabled(m?.enabled ?? true)
      setPub(m?.public ?? true)
      setF({
        display_name: m?.display_name ?? '',
        description: m?.description ?? '',
        prompt_price: num(m?.prompt_price ?? '0'),
        completion_price: num(m?.completion_price ?? '0'),
        cache_read_price: num(m?.cache_read_price ?? '0'),
        cache_write_price: num(m?.cache_write_price ?? '0'),
        price_per_image: num(m?.price_per_image ?? '0'),
        default_max_tokens: String(m?.default_max_tokens ?? 4096),
        max_input_chars: String(m?.max_input_chars ?? 1_000_000),
        sort: String(m?.sort ?? 0),
        pricing_tiers: showJson(m?.pricing_tiers, !m?.pricing_tiers?.length),
        peak_pricing: showJson(m?.peak_pricing, !m?.peak_pricing),
        promotional_pricing: showJson(m?.promotional_pricing, !m?.promotional_pricing),
        image_token_prices: showJson(m?.image_token_prices, isEmptyObj(m?.image_token_prices)),
        video_pricing: showJson(m?.video_pricing, isEmptyObj(m?.video_pricing)),
      })
    }
  }
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const bad = (name: string) => t(A.models.badJson, { name })

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const body: ModelInput = {
        kind,
        display_name: f.display_name.trim(),
        description: f.description.trim(),
        price_currency: currency,
        prompt_price: f.prompt_price.trim() || '0',
        completion_price: f.completion_price.trim() || '0',
        cache_read_price: f.cache_read_price.trim() || '0',
        cache_write_price: f.cache_write_price.trim() || '0',
        price_per_image: f.price_per_image.trim() || '0',
        image_billing_mode: imageMode,
        image_token_prices: parseJson(t(A.models.imageTokenPrices), f.image_token_prices, {}, bad),
        video_pricing: parseJson(t(A.models.videoPricing), f.video_pricing, {}, bad),
        pricing_tiers: parseJson(t(A.models.tiers), f.pricing_tiers, null, bad),
        peak_pricing: parseJson(t(A.models.peak), f.peak_pricing, null, bad),
        promotional_pricing: parseJson(t(A.models.promo), f.promotional_pricing, null, bad),
        thinking_policy: kind === 'chat' ? thinking : 'inherit',
        default_max_tokens: Number(f.default_max_tokens),
        max_input_chars: Number(f.max_input_chars),
        enabled,
        public: pub,
        sort: Number(f.sort || 0),
      }
      const r = await catalogApi.putModel(id.trim(), body)
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved()
    } catch (e) {
      setError(e instanceof Error && !('code' in e) ? e.message : errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const price = (k: string, label: string) => (
    <Field label={label}>
      <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]+)?" required value={f[k] ?? '0'} onChange={set(k)} />
    </Field>
  )
  const json = (k: string, label: string, rows = 4) => (
    <Field label={label}>
      <Textarea rows={rows} spellCheck={false} className="ap-mono" value={f[k] ?? ''} onChange={set(k)} />
    </Field>
  )
  const textual = kind !== 'image' && kind !== 'video'

  return (
    <Modal open={model !== null} onClose={onClose} busy={busy} wide title={isNew ? t(A.models.create) : id}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="ap-grid2">
          <Field label={t(A.models.id)} hint={isNew ? t(A.models.idHint) : undefined}>
            <Input mono required disabled={!isNew} pattern="[A-Za-z0-9][A-Za-z0-9._:/\-]{0,119}" value={id} onChange={(e) => setId(e.target.value)} />
          </Field>
          <Field label={t(A.models.kind)} hint={isNew ? undefined : t(A.models.kindLocked)}>
            <Select value={kind} disabled={!isNew} onChange={(e) => setKind(e.target.value as ModelKind)} options={KINDS.map((k) => ({ value: k, label: k }))} />
          </Field>
          <Field label={t(A.models.displayName)}>
            <Input maxLength={120} value={f.display_name ?? ''} onChange={set('display_name')} />
          </Field>
          <Field label={t(A.models.sort)}>
            <Input type="number" value={f.sort ?? '0'} onChange={set('sort')} />
          </Field>
        </div>
        <Field label={t(A.models.description)}>
          <Textarea rows={2} maxLength={2000} value={f.description ?? ''} onChange={set('description')} />
        </Field>
        <div className="ap-grid2">
          <Field label={t(A.models.currency)}>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'CNY')} options={[{ value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' }]} />
          </Field>
          {kind === 'chat' && (
            <Field label={t(A.models.thinking)} hint={t(A.models.thinkingHint)}>
              <Select
                value={thinking}
                onChange={(e) => setThinking(e.target.value as ThinkingPolicy)}
                options={[
                  { value: 'inherit', label: t(A.models.thinkInherit) },
                  { value: 'force_on', label: t(A.models.thinkOn) },
                  { value: 'force_off', label: t(A.models.thinkOff) },
                ]}
              />
            </Field>
          )}
        </div>
        {kind === 'image' && (
          <div className="ap-grid2">
            <Field label={t(A.models.imageMode)}>
              <Select
                value={imageMode}
                onChange={(e) => setImageMode(e.target.value as 'per_image' | 'token')}
                options={[
                  { value: 'per_image', label: t(A.models.imageModePerImage) },
                  { value: 'token', label: t(A.models.imageModeToken) },
                ]}
              />
            </Field>
            {imageMode === 'per_image' && price('price_per_image', t(A.models.perImage))}
          </div>
        )}
        {textual && (
          <div className="ap-grid2">
            {price('prompt_price', t(A.models.promptPrice))}
            {price('completion_price', t(A.models.completionPrice))}
            {price('cache_read_price', t(A.models.cacheReadPrice))}
            {price('cache_write_price', t(A.models.cacheWritePrice))}
          </div>
        )}
        <div className="ap-grid2">
          <Field label={t(A.models.maxTokens)}>
            <Input type="number" min={1} max={1_000_000} required value={f.default_max_tokens ?? ''} onChange={set('default_max_tokens')} />
          </Field>
          <Field label={t(A.models.maxChars)}>
            <Input type="number" min={1} max={50_000_000} required value={f.max_input_chars ?? ''} onChange={set('max_input_chars')} />
          </Field>
        </div>
        <Card title={t(A.models.advanced)}>
          <div className="ap-stack" style={{ gap: 12 }}>
            <span className="ap-small ap-muted">{t(A.models.advancedHint)}</span>
            {textual && json('pricing_tiers', t(A.models.tiers))}
            {textual && json('peak_pricing', t(A.models.peak))}
            {json('promotional_pricing', t(A.models.promo))}
            {kind === 'image' && json('image_token_prices', t(A.models.imageTokenPrices))}
            {kind === 'video' && json('video_pricing', t(A.models.videoPricing), 6)}
          </div>
        </Card>
        <div className="ap-row">
          <Switch checked={enabled} onChange={setEnabled} label={t(A.models.enabled)} />
          <Switch checked={pub} onChange={setPub} label={t(A.models.public)} />
        </div>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

